<template>
    <v-dialog v-model="dialogComputed" >
        <v-card max-width="900px" min-width="600px" class="custom-positioned-dialog">
            <v-card-title>
                <v-icon icon="mdi-account-multiple-outline"></v-icon>
                Assign ({{ selectedSubItems.length }})

                <v-tooltip text="Select All" location="bottom">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props"
                               @click="selectAllUsers"
                               variant="text">
                            <v-icon>mdi-select-all</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>
                <v-tooltip text="Deselect All" location="bottom">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props"
                               @click="deselectAllUsers"
                               variant="text">
                            <v-icon>mdi-select-off</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="12">
                        <v-alert type="info" variant="tonal">
                            Select one or more users to assign to
                        </v-alert>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="12">
                        <v-card height="100%" class="overflow-y-auto" >
                            <v-list>
                                <v-list-item 
                                             v-for="user in teamUsersObj[localSelectedTeam]"
                                             :key="user.id"
                                             @click="toggleUserSelection(user.id)">
                                    <template v-slot:prepend>
                                        <v-checkbox v-model="selectedUsers"
                                                    :value="user.id"
                                                    density="compact"
                                                    hide-details
                                                    class="ma-0 pa-0 mr-2"
                                                    @click.stop></v-checkbox>
                                        <v-avatar :size="28"
                                                  class="mr-1">
                                            <v-img v-if="user.avatarUrl"
                                                   :src="'https://' + user.avatarUrl"
                                                   alt="User Avatar"></v-img>
                                            <span v-else
                                                  class="d-flex justify-center align-center text-caption white--text"
                                                  :style="{
                                backgroundColor: getRandomColor(user.id),
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%'
                              }">
                                                {{ getInitials(user.displayName) }}
                                            </span>
                                        </v-avatar>
                                    </template>
                                    <v-list-item-title>{{ user.displayName }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ user.roleTitle }}</v-list-item-subtitle>
                                    <template v-slot:append>
                                        <v-chip>{{ getUserItemCount(user.id) }}</v-chip>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey" text @click="closeDialog">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="assignTeam">
                    Assign
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="progressDialog" persistent max-width="300">
    <v-card>
      <v-card-text>
        Assigning items...
        <v-progress-linear
          indeterminate
          color="primary"
          class="mb-0"
        ></v-progress-linear>
      </v-card-text>
    </v-card>
  </v-dialog>
  </template>
  
  <script setup>
  import { ref, computed, defineProps, defineEmits} from 'vue';
  import { useStore } from 'vuex';
  import { useAnalytics } from '@/utils/useAnalytics'

  const store = useStore();
  const { trackEvent } = useAnalytics()


  const props = defineProps(['dialog', 'selectedSubItems', 'selectedTeam']);
  const emit = defineEmits(['update:dialog', 'update:selectedSubItems']);

  const dialogComputed = computed({
    get: () => props.dialog,
    set: (value) => emit('update:dialog', value)
  });
  
  const teamUsersObj = computed(() => store.getters.getTeamUsersObj);
  const items = computed(() => store.getters.getItems);
  const filterContext = computed(() => store.state.filterItemsContext);
  const settings = computed(() => store.getters.getSettings);

  const selectedSubItems = computed(() => props.selectedSubItems);
  
  const progressDialog = ref(false);
  
  const localSelectedTeam = ref(String(props.selectedTeam));
  const selectedUsers = ref([]);

  const getInitials = (name) => {
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      return words[0].substring(0, 2).toUpperCase();
    }
  };
  
  const getRandomColor = (id) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
    const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };


  const getUserItemCount = (userId) => {
    let count = 0;
    
    items.value.forEach(item => {
      if (item.subItems) {
        item.subItems.forEach(subItem => {
          if (subItem.assigneeUserIds && subItem.assigneeUserIds.includes(userId)) {
            count += 1;
          }
        });
      }
    });

    return count;
  };
  
  const closeDialog = () => {
    dialogComputed.value = false;
  };
  
  const assignTeam = async () => {
    progressDialog.value = true;

    const putObj = {
      teamId: String(localSelectedTeam.value),
      userIds: selectedUsers.value.map(String)
    };

    try {

      trackEvent('assignUsers', {
        action_type: 'button_click',
        apiUrl: store.state.apiClient.apiUrl,
        project_code: store.getters.getProjectCode,
        project_name: store.getters.getProjectName,
      })

      const updatedItems = filterContext.value.originalItems.map(item => {
        // Check if this item contains any selected subItems
        const hasSelectedSubItems = item.subItems.some(subItem => 
          selectedSubItems.value.includes(subItem.id)
        );

        // If no selected subItems in this item, return the original item
        if (!hasSelectedSubItems) return item;

        // If there are selected subItems, create new item with updated subItems
        return {
          ...item,
          subItems: item.subItems.map(async subItem => {
            if (!selectedSubItems.value.includes(subItem.id)) return subItem;

            // Update the API for this subItem
            const itemType = item.type === 'IssueTemplate' ? 'issues' : 'forms';
            await store.state.apiClient.put(
              `/api/${settings.value.projectCode}/${itemType}/${subItem.id}/assignee`,
              putObj
            );

            // Return updated subItem
            return {
              ...subItem,
              assigneeTeamId: putObj.teamId,
              assigneeUserIds: putObj.userIds
            };
          })
        };
      });

      // Wait for all API calls to complete
      const resolvedItems = await Promise.all(
        updatedItems.map(async item => ({
          ...item,
          subItems: await Promise.all(item.subItems)
        }))
      );

      // Update the context with the resolved items
      filterContext.value.updateItems(resolvedItems);

      emit('update:selectedSubItems', []);
      
      // Close both dialogs
      closeDialog();
      progressDialog.value = false;
    } catch (error) {
      console.error("Error assigning items:", error);
      // You might want to show an error message to the user here
    } finally {
      progressDialog.value = false;
    }
  };

  const toggleUserSelection = (userId) => {
    const index = selectedUsers.value.indexOf(userId);
    if (index === -1) {
      selectedUsers.value.push(userId);
    } else {
      selectedUsers.value.splice(index, 1);
    }
  };

  const selectAllUsers = () => {
    const teamUsers = teamUsersObj.value[localSelectedTeam.value] || [];
    selectedUsers.value = teamUsers.map(user => user.id);
  };

  const deselectAllUsers = () => {
    selectedUsers.value = [];
  };

  </script>
  
  <style scoped>
  .v-list-group__items .v-list-item {
    padding-inline-start: 16px !important;
  }
  .v-list-item {
    cursor: pointer;
  }

      .custom-positioned-dialog {
          position: relative;
          align-self: center;
          max-height: 90vh;
          margin-top: -9%;
      }
  </style>